import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { DashboardService } from '../../../services/dashboard.service';
import { CrudService } from '../../../services/crud.service';
import { ToastrService } from 'ngx-toastr';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-voice-modal',
  templateUrl: './voice-modal.component.html',
  styleUrl: './voice-modal.component.css'
})
export class VoiceModalComponent {
  @ViewChild('mySearchInput') mySearchInput!: ElementRef;

  deepgramSocket: WebSocket | null = null;
  audioProcessor: any = null;
  options = {
    controls: true,
    width: 600,
    height: 80,
    plugins: {
      wavesurfer: {
        backend: 'WebAudio',
        waveColor: '#36393b',
        progressColor: 'black',
        debug: false,
        cursorWidth: 1,
        hideScrollbar: true
      },
      record: {
        audio: true,
        video: false,
        maxLength: 60,
        debug: true
      }
    }
  };

  isRecording = false;
  mediaRecorder: any;
  audioChunks: any[] = [];
  audioUrl: string | null = null;
  audioContext: AudioContext | null = null;
  analyser: AnalyserNode | null = null;
  dataArray: Uint8Array | null = null;
  animationId: any;
  stream: MediaStream | null = null;
  isListeningPrompt = false;
  isErrorPrompt = false;
  searchTerm: any;
  spinner: boolean = false;

  constructor(
    private crudService: CrudService,
    private dashboardService: DashboardService,
    private toaster: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<VoiceModalComponent>
  ) {
    this.spinner = true;
    this.startVoiceRecording();
  }

  async startVoiceRecording() {

    // console.log("in startVoiceRecording");

    try {
      this.audioChunks = [];
      let speechDetected = false;

      //Requesting microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      //const mimeType = 'audio/webm';
      const mimeType = 'audio/webm; codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        this.toaster.error(`Your browser does not support the required audio format of ${mimeType}`);
        return;
      }

      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });

      // Set a 1-minute timeout to check for no speech
      const speechTimeout = setTimeout(() => {
        if (!speechDetected) {
          this.stopRecording(); // Ensure cleanup
          this.isListeningPrompt = false;
          this.isErrorPrompt = true;

          setTimeout(() => {
            this.dialogRef.close({ searchTerm: null });
          }, 10000)
          
        }
      }, 10000); // 10 secs

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = async () => {

        // Finalize the recording
        const mimeType = 'audio/webm'; // Ensure it's declared or scoped appropriately
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });
        this.audioUrl = URL.createObjectURL(audioBlob);
        this.stream.getTracks().forEach(track => track.stop());
        this.isRecording = false;

        // console.log('Recording complete, audio URL:', this.audioUrl);

        try {
          // Convert Blob to ArrayBuffer
          const arrayBuffer = await audioBlob.arrayBuffer();

          this.crudService.transcribeAudioStream(arrayBuffer).subscribe((response: any) => {
            // console.log('*** Transcription Response = ', response);
            this.searchTerm = response.data;

            // console.log('*** this.transcript = ', this.searchTerm);
            this.dialogRef.close({searchTerm: this.searchTerm});
            
            if (this.searchTerm == "")
              this.isErrorPrompt = true;

          }, (error: any) => {
            console.error('Error during transcription:', error);
            this.isErrorPrompt = true;
          });

        } catch (err) {
          console.error('Error during transcription:', err);
          this.isErrorPrompt = true;
        }
      };

      this.mediaRecorder.start();
      this.isRecording = true;
      this.spinner = false
      this.isListeningPrompt = false;
      this.isErrorPrompt = false

      // Show "Speak now..." for 1 second, then switch to "Listening..."
      setTimeout(() => {
        this.isListeningPrompt = true;
      }, 1000);

      this.startVisualizer();
    } catch (err) {
      // console.log('in new-header.startVoiceRecording - err: ', err);
      this.isRecording = false;
      this.spinner = false;
      this.dialogRef.close();
      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        this.toaster.error('No microphone found. Please connect a microphone and try again.');
      } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        this.toaster.error('Microphone access was denied. Please allow access and try again.');
      } else {
        this.toaster.error('An error occurred: ' + err.message);
      }
    }
  }


  stopRecording() {
    // console.log("in stopRecording");
    this.spinner = true;
    this.isRecording = false;
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    // if (this.deepgramSocket && this.deepgramSocket.readyState === WebSocket.OPEN) {
      //   this.deepgramSocket.close();
      // }
      if (this.audioProcessor) {
        this.audioProcessor.disconnect();
        this.audioProcessor = null;
      }
      // this.spinner = false
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  closeModal() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    this.isRecording = false;
    this.spinner = false;
    this.audioUrl = null;
    this.searchTerm = '';

    if (this.deepgramSocket && this.deepgramSocket.readyState === WebSocket.OPEN) {
      this.deepgramSocket.close();
    }
    if (this.audioProcessor) {
      this.audioProcessor.disconnect();
      this.audioProcessor = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.dialogRef.close();
  }


  // --- Waveform Visualizer Methods ---
  startVisualizer() {
    if (!this.stream || !this.audioContext) return;
    const source = this.audioContext.createMediaStreamSource(this.stream);
    this.analyser = this.audioContext.createAnalyser();
    source.connect(this.analyser);
    this.analyser.fftSize = 256;
    const bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(bufferLength);
    // You can add visualization logic here
  }


  resetRecording() {
    // Stop any ongoing recording
    this.mediaRecorder.stop();
    // if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
    // }

    // Reset audio chunks and audio URL
    this.audioChunks = [];
    this.audioUrl = null;
    this.isRecording = false;

    // Start fresh recording
    this.startVoiceRecording();
  }

  triggerSearch() {
    const el = this.mySearchInput.nativeElement;
    const event = new KeyboardEvent('keyup', {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
      bubbles: true
    });
    el.dispatchEvent(event);
  }
}
